import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/context/AuthContext'
import { setPassword } from '@/api/auth'

const schema = z.object({
  new_password: z.string().min(8, '8文字以上にしてください'),
  new_password2: z.string(),
}).refine((d) => d.new_password === d.new_password2, {
  message: 'パスワードが一致しません',
  path: ['new_password2'],
})

type FormValues = z.infer<typeof schema>

export default function SetPasswordPage() {
  const navigate = useNavigate()
  const { user, updateTokens, refreshUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      const { data } = await setPassword(values.new_password, values.new_password2)
      updateTokens(data.access, data.refresh)
      await refreshUser()
      toast.success('パスワードを設定しました')
      navigate('/settings', { replace: true })
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? 'パスワードの設定に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-[env(safe-area-inset-top)] z-10 flex h-14 items-center bg-background border-b px-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mr-2 flex items-center justify-center min-h-[44px] min-w-[44px] -ml-2"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-base font-semibold">パスワードを設定</h1>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {user?.email && (
          <div className="rounded-lg border bg-card px-4 py-3">
            <p className="text-xs text-muted-foreground">メールアドレス</p>
            <p className="text-sm font-medium mt-0.5">{user.email}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">新しいパスワード（8文字以上）</label>
            <Input {...register('new_password')} type="password" autoComplete="new-password" />
            {errors.new_password && <p className="text-xs text-destructive">{errors.new_password.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">パスワード（確認）</label>
            <Input {...register('new_password2')} type="password" autoComplete="new-password" />
            {errors.new_password2 && <p className="text-xs text-destructive">{errors.new_password2.message}</p>}
          </div>

          <Button type="submit" className="w-full mt-2" disabled={isLoading}>
            {isLoading ? '設定中...' : 'パスワードを設定'}
          </Button>
        </form>
      </div>
    </div>
  )
}
